import { motion } from "framer-motion";
import { ChevronRight, BookOpen, Shield, Users, Scale, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

interface ToolCardsProps {
  setSelectedTool: (tool: string) => void;
}

const ToolCards = ({
  setSelectedTool
}: ToolCardsProps) => {
  const navigate = useNavigate();
  const handleToolSelect = (tool: string) => {
    // Navigate to the hash and then set the selected tool
    navigate(`/apologetics#${tool}`);
    setSelectedTool(tool);
  };

  return <motion.div 
    initial={{ opacity: 0, y: 20 }} 
    animate={{ opacity: 1, y: 0 }} 
    transition={{ duration: 0.8, delay: 0.4 }} 
    className="max-w-7xl mx-auto mb-16"
  >
    <h1 className="font-playfair font-bold text-center mb-8 text-gray-900 pt-24 py-[88px] my-0 text-3xl sm:text-5xl md:text-7xl whitespace-nowrap">
      - The <span className="text-heaven-gold">Apologetics</span> Suite -
    </h1>
    
    
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 -mt-4 md:-mt-0">
        {/* Biblical Defense Tool */}
        <Card className="bg-white border border-heaven-gold/10 shadow-md shadow-heaven-gold/10 hover:shadow-xl hover:shadow-heaven-gold/20 transition-all overflow-hidden flex flex-col">
          <div className="relative h-44 overflow-hidden">
            <img src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//addrress%20atheism.png" alt="Addressing Atheism Tool" className="w-full h-full object-cover" />
          </div>
          <CardHeader className="p-4">
            <div className="flex flex-col items-center mb-3">
              <div className="flex justify-center mb-2">
                <BookOpen className="w-8 h-8 text-[#1EAEDB]" />
              </div>
              <CardTitle className="text-[28px] font-playfair text-gray-900 text-center">Theology Answers Tool</CardTitle>
            </div>
            <CardDescription className="text-heaven-gold font-bold text-sm text-center">
              Tackle life's most difficult spiritual questions
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex-grow">
            <p className="text-[#403E43] text-sm mb-4">
              From suffering and doubt to the nature of God and the reliability of Scripture. 
              Get long-form, structured answers using theology, reason, and the Word.
            </p>
          </CardContent>
          <CardFooter className="p-4 pt-0">
            <Button variant="outline" className="w-full bg-white border-heaven-gold text-heaven-gold hover:bg-heaven-gold/5" onClick={() => handleToolSelect("biblical-defense")}>
              Access Tool <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
        
        {/* Addressing Atheism Tool */}
        <Card className="bg-white border border-heaven-gold/10 shadow-md shadow-heaven-gold/10 hover:shadow-xl hover:shadow-heaven-gold/20 transition-all overflow-hidden flex flex-col">
          <div className="relative h-44 overflow-hidden">
            <img src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//biblical%20defense%20ai.png" alt="Biblical Defense Tool" className="w-full h-full object-cover" />
          </div>
          <CardHeader className="p-4">
            <div className="flex flex-col items-center mb-3">
              <div className="flex justify-center mb-2">
                <Shield className="w-8 h-8 text-[#1EAEDB]" />
              </div>
              <CardTitle className="text-[28px] font-playfair text-gray-900 text-center">Addressing Atheism Tool</CardTitle>
            </div>
            <CardDescription className="text-heaven-gold font-bold text-sm text-center">
              Handle the toughest objections from skeptics
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex-grow">
            <p className="text-[#403E43] text-sm mb-4">
              From the problem of evil to science vs. faith, you'll receive clear, logical, 
              Scripture-based responses with compassion and confidence.
            </p>
          </CardContent>
          <CardFooter className="p-4 pt-0">
            <Button variant="outline" className="w-full bg-white border-heaven-gold text-heaven-gold hover:bg-heaven-gold/5" onClick={() => handleToolSelect("atheism")}>
              Access Tool <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
        
        {/* Interfaith Dialogue Tool */}
        <Card className="bg-white border border-heaven-gold/10 shadow-md shadow-heaven-gold/10 hover:shadow-xl hover:shadow-heaven-gold/20 transition-all overflow-hidden flex flex-col">
          <div className="relative h-44 overflow-hidden">
            <img src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Why%20(4).png" alt="Interfaith Dialogue Tool" className="w-full h-full object-cover" />
          </div>
          <CardHeader className="p-4">
            <div className="flex flex-col items-center mb-3">
              <div className="flex justify-center mb-2">
                <Users className="w-8 h-8 text-[#1EAEDB]" />
              </div>
              <CardTitle className="text-[28px] font-playfair text-gray-900 text-center">Interfaith Dialogue Tool</CardTitle>
            </div>
            <CardDescription className="text-heaven-gold font-bold text-sm text-center">
              Engage other religions with biblical truth
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex-grow">
            <p className="text-[#403E43] text-sm mb-4">
              From Islam to Hinduism, from Judaism to New Age spirituality, learn to defend 
              the gospel against competing claims with Scripture, reason, and grace.
            </p>
          </CardContent>
          <CardFooter className="p-4 pt-0">
            <Button variant="outline" className="w-full bg-white border-heaven-gold text-heaven-gold hover:bg-heaven-gold/5" onClick={() => handleToolSelect("interfaith-dialogue")}>
              Access Tool <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
        
        {/* Moral & Ethical Dilemmas Tool */}
        <Card className="bg-white border border-heaven-gold/10 shadow-md shadow-heaven-gold/10 hover:shadow-xl hover:shadow-heaven-gold/20 transition-all overflow-hidden flex flex-col">
          <div className="relative h-44 overflow-hidden">
            <img src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//moral%20dilemma.png" alt="Moral & Ethical Dilemmas Tool" className="w-full h-full object-cover" />
          </div>
          <CardHeader className="p-4">
            <div className="flex flex-col items-center mb-3">
              <div className="flex justify-center mb-2">
                <Scale className="w-8 h-8 text-[#1EAEDB]" />
              </div>
              <CardTitle className="text-[28px] font-playfair text-gray-900 text-center">Moral & Ethical Dilemmas Tool</CardTitle>
            </div>
            <CardDescription className="text-heaven-gold font-bold text-sm text-center">
              Navigate today's biggest cultural questions
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex-grow">
            <p className="text-[#403E43] text-sm mb-4">
              Whether the topic is identity, justice, gender, politics, or relationships — 
              Selah helps you find clear answers rooted in Scripture.
            </p>
          </CardContent>
          <CardFooter className="p-4 pt-0">
            <Button variant="outline" className="w-full bg-white border-heaven-gold text-heaven-gold hover:bg-heaven-gold/5" onClick={() => handleToolSelect("moral-dilemmas")}>
              Access Tool <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
        
        {/* Evangelism Training Tool */}
        <Card className="md:col-span-2 bg-white border border-heaven-gold/10 shadow-md shadow-heaven-gold/10 hover:shadow-xl hover:shadow-heaven-gold/20 transition-all overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/3 relative overflow-hidden h-44 md:h-auto">
              <img src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//apolo%20ai.png" alt="Evangelism Training" className="w-full h-full object-cover" />
            </div>
            <div className="md:w-2/3 p-4 flex flex-col">
              <div className="flex-grow">
                <div className="flex flex-col items-center mb-3">
                  <div className="flex justify-center mb-2">
                    <MessageSquare className="w-8 h-8 text-[#1EAEDB]" />
                  </div>
                  <h3 className="text-[28px] font-playfair font-bold text-gray-900 text-center">
                    Evangelism Training Tool
                  </h3>
                </div>
                <p className="text-heaven-gold font-bold text-sm mb-3 text-center">
                  Practice real conversations with realistic AI simulations
                </p>
                <p className="text-[#403E43]/80 text-sm mb-3">
                  Practice real conversations with atheists, Muslims, Hindus, and more — all simulated with intelligent, 
                  reactive AI. Share your faith, answer tough questions, and learn how to lead someone to Christ in real time.
                </p>
              </div>
              <div className="mt-4">
                <Button variant="outline" className="w-full bg-white border-heaven-gold text-heaven-gold hover:bg-heaven-gold/5" onClick={() => handleToolSelect("evangelism-assistant")}>
                  Access Tool <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    
  </motion.div>;
};

export default ToolCards;
